import clsx from "clsx"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import TableCell from "@material-ui/core/TableCell"
import Checkbox from "@material-ui/core/Checkbox"
import Select from "@material-ui/core/Select"
import MenuItem from "@material-ui/core/MenuItem"
import Autocomplete from "@material-ui/lab/Autocomplete"
import TextField from "@material-ui/core/TextField"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"

import ClearIcon from "@material-ui/icons/Clear"
import AddIcon from "@material-ui/icons/Add"

import { SearchTextField } from "components/partials"
import { roles } from "../utils/common"
import { ActionCreators, useAdminState, useAdminDispatch } from "../Context"
import { AdminHeaderForm } from "components/partials"
import { UserRoleChip } from "components/widgets"
import { useAdminFilter } from "common/hooks"
import { CompaniesApi } from "common/api"
import {
  getFormTitle,
  getFilterPlaceholder
} from "common/helpers/admin-page-func.helper"
import useStyles from "./Table.style"

const Header = props => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { rows } = useAdminState()
  const dispatch = useAdminDispatch()
  const [filter, onChangeFilter] = useAdminFilter()
  const [companies, setCompanies] = useState([])

  useEffect(() => {
    CompaniesApi.get().then(response => {
      setCompanies(response.data.map(company => company.name))
    })
  }, [])

  function onToggleSelectAll(event) {
    const { checked } = event.target
    dispatch(
      checked ? ActionCreators.selectAll() : ActionCreators.deselectAll()
    )
  }

  function onChangeCompany(event, value) {
    if (!event) return // Fix for clearing all filters

    const target = { ...event.target, value }
    onChangeFilter("company")({
      ...event,
      target
    })
  }

  function onClickCreate() {
    dispatch(ActionCreators.setToEdit({ verified: false, enabled: true }))
  }

  const totalSelected = rows.filter(({ isSelected }) => isSelected).length
  const totalRows = rows.length

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={totalSelected > 0 && totalSelected < totalRows}
            checked={totalRows > 0 && totalSelected === totalRows}
            onChange={onToggleSelectAll}
          />
        </TableCell>

        <TableCell>
          <AdminHeaderForm
            label={t("view.admin.name")}
            SortProps={{
              key: "fullName"
            }}
          >
            <SearchTextField
              fullWidth
              placeholder={getFilterPlaceholder(t("view.admin.name"))}
              value={filter.fullName || ""}
              onChange={onChangeFilter("fullName")}
            />
          </AdminHeaderForm>
        </TableCell>

        <TableCell>
          <AdminHeaderForm
            label={t("view.admin.email")}
            SortProps={{
              key: "email"
            }}
          >
            <SearchTextField
              fullWidth
              placeholder={getFilterPlaceholder(t("view.admin.email"))}
              value={filter.email || ""}
              onChange={onChangeFilter("email")}
            />
          </AdminHeaderForm>
        </TableCell>

        <TableCell style={{ minWidth: 200, width: "1%", whiteSpace: "nowrap" }}>
          <AdminHeaderForm
            label={t("view.admin.role")}
            SortProps={{
              key: "role"
            }}
          >
            <Select
              fullWidth
              displayEmpty
              value={filter.role || ""}
              onChange={onChangeFilter("role")}
              className={clsx({ [classes.grey]: !filter.role })}
            >
              <MenuItem value="">
                {getFilterPlaceholder(t("view.admin.role"))}
              </MenuItem>
              {roles.map((role, index) => (
                <MenuItem key={index} value={role}>
                  <UserRoleChip role={role} />
                </MenuItem>
              ))}
            </Select>
          </AdminHeaderForm>
        </TableCell>

        {props.showCompany && (
          <TableCell>
            <AdminHeaderForm
              label={t("view.admin.company")}
              SortProps={{
                key: "company"
              }}
            >
              <Autocomplete
                freeSolo
                style={{ minWidth: 150 }}
                options={["Unassigned", ...companies]}
                value={filter.company || ""}
                onInputChange={onChangeCompany}
                closeIcon={<ClearIcon color="primary" />}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={getFilterPlaceholder(t("view.admin.company"))}
                    InputProps={{ ...params.InputProps }}
                  />
                )}
              />
            </AdminHeaderForm>
          </TableCell>
        )}

        <TableCell style={{ minWidth: 200, width: "1%", whiteSpace: "nowrap" }}>
          <AdminHeaderForm
            label={t("view.admin.verified")}
            SortProps={{
              key: "verified"
            }}
          >
            <Select
              fullWidth
              displayEmpty
              value={filter.verified || ""}
              onChange={onChangeFilter("verified")}
              className={clsx({ [classes.grey]: !filter.verified })}
            >
              <MenuItem value="" selected>
                {getFilterPlaceholder(t("view.admin.verified"))}
              </MenuItem>
              <MenuItem value="true">True</MenuItem>
              <MenuItem value="false">False</MenuItem>
            </Select>
          </AdminHeaderForm>
        </TableCell>

        {props.showEnabled && (
          <TableCell
            style={{ minWidth: 200, width: "1%", whiteSpace: "nowrap" }}
          >
            <AdminHeaderForm
              label={t("view.adminUsers.filterEnabled")}
              SortProps={{
                key: "enabled"
              }}
            >
              <Select
                fullWidth
                displayEmpty
                value={filter.enabled || ""}
                onChange={onChangeFilter("enabled")}
                className={clsx({ [classes.grey]: !filter.enabled })}
              >
                <MenuItem value="" selected>
                  {getFilterPlaceholder(t("view.adminUsers.filterEnabled"))}
                </MenuItem>
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
            </AdminHeaderForm>
          </TableCell>
        )}

        <TableCell className={classes.cellFit}>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            {t("view.admin.actions")}
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onClickCreate}
            >
              {getFormTitle(t("view.admin.user"))}
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    </TableHead>
  )
}

export default Header
